import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';
import {  UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PurlQuestion, Account, AccountPurlSettings } from '../../../../models';
import { AccountSettingsService, MapKeyService, QuestionService } from '../../../../services';
import * as _ from 'lodash';
import { RichTextEditorComponent } from '@Shared/components/rich-text-editor/rich-text-editor.component';
import { AppService } from '../../../../app.service';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { MapKeysDTO } from '@DTOs';
import { requirementDropdown1 } from '@TestUtils/test-data.spec';
import { MapKeysDropdownComponent } from '@Components/map-keys-dropdown/map-keys-dropdown.component';
import { AngularEditorConfig, AngularEditorModule } from '@kolkov/angular-editor';

@Component({
    selector: 'purl-settings',
    templateUrl: './purl-settings.component.html',
    imports: [SharedModule, RichTextEditorComponent, MapKeysDropdownComponent, AngularEditorModule]
})
export class PurlSettingsComponent implements OnInit {
  @Input() accountInfo: Account;
  @ViewChild('expirationMessageEditor') expirationMessageEditor: RichTextEditorComponent;
  @ViewChild('verificationAttemptsMessageEditor') verificationAttemptsMessageEditor: RichTextEditorComponent;
  @ViewChild('mfaSecurityCodeEmailBodyEditor') mfaSecurityCodeEmailBodyEditor: RichTextEditorComponent;
  @ViewChild('mfaSecurityCodeSMSBodyEditor') mfaSecurityCodeSMSBodyEditor: RichTextEditorComponent;
  @ViewChild('mfaErrorMessageBodyEditor') mfaErrorMessageBodyEditor: RichTextEditorComponent;
  @ViewChild('mfaEmailContentEditor') mfaEmailContentEditor: RichTextEditorComponent;

  showForm = false;
  purlSettingsForm: UntypedFormGroup;
  accountSetting: any = {};
  purlSettings: AccountPurlSettings;
  questions;
  expirationMessage = ' ';
  verificationAttemptsMessage = ' ';
  purlEnabled = false;
  requireMFA = false;
  mfaEmailEnabled = false;
  mfaSMSEnabled = false;
  charsRemaining = 0;

  mapKeys: MapKeysDTO[];

  richTextEditorConfig: AngularEditorConfig = {
      height: '6rem',
      minHeight: '5rem',
      defaultFontName: 'Arial',
      toolbarHiddenButtons: [[
          'insertImage', 'insertVideo', 'textColor', 'backgroundColor', 'customClasses',
          'link', 'unlink', 'insertHorizontalRule', 'removeFormat'
      ]]
  };

  editorConfig: AngularEditorConfig = {
        editable: true,
        spellcheck: true,
        height: '225px',
        minHeight: '0',
        maxHeight: 'auto',
        width: 'auto',
        minWidth: '0',
        translate: 'yes',
        enableToolbar: true,
        showToolbar: true,
        sanitize: false,
        toolbarHiddenButtons:[
            ['insertImage',
                'insertVideo',
                'customClasses',
                'insertHorizontalRule',
                'removeFormat',]
        ]
    };
  constructor(
    public accountSettingService: AccountSettingsService,
    public appService: AppService,
    public questionService: QuestionService,
    public mapkeysService: MapKeyService,
    private _fb: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
      this.purlSettingsForm = this._fb.group({
          isPurlEnabled: false,
          requireMFA: false,
          numberOfRetries: ['3', [Validators.required, Validators.max(10), Validators.min(1)]],
          expirationDays: ['30', [Validators.required, Validators.max(90), Validators.min(1)]],
          welcomeBodyText: ['To protect your information, we need to ask you a few questions before you can access your application.', [Validators.required]],
          welcomeHeaderText: ['Please verify your identity', [Validators.required]],
          verificationQuestions: this._fb.array([], Validators.required),
          mfaQuestionText: ['How do you want to receive your security code?'],
          mfaExpirationTime: ['10'],
          mfaTokenMapkey: [''],
          mfaEmailEnabled: [''],
          mfaEmailSubject: [''],
          mfaEmailQuestionLabel: ['Email me'],
          mfaEmailMapkey: [''],
          mfaSMSEnabled: [''],
          mfaSMSQuestionLabel: ['Send me a text message'],
          mfaSMSMapkey: [''],
          mfaSMSContent:  [''],
          mfaSecurityCodeTitle:  [''],
          mfaErrorMessageTitle:  ['']

      });

      this.mapKeys = this.mapkeysService.mapKeys;

      this.getQuestions();
      this.loadAccountSetting();
  }

  get verificationQuestions() {
      return this.purlSettingsForm.controls["verificationQuestions"] as UntypedFormArray;
  }

  getQuestions() {
      this.questionService.getQuestions(this.accountInfo).subscribe(result => {
          this.questions = result.filter(x => x.mapKey && x.isRequired === true);

          this.questions = _.sortBy(this.questions, 'mapKey.entityHierarchy');
      });
  }

  loadAccountSetting() {
      this.appService.getData(`AccountSettings/${this.accountInfo.id}`).subscribe(
          response => {
              if (response.status === 'success') {
                  const setting = _.find(response.data, { 'name': 'purlSettings' });
                  if (setting) {
                      this.accountSetting = setting;
                      this.purlSettings = JSON.parse(setting.value);
                      this.purlEnabled = this.purlSettings.isPurlEnabled;
                      this.requireMFA = this.purlSettings.requireMFA;
                      this.mfaEmailEnabled = this.purlSettings.mfaEmailEnabled;
                      this.mfaSMSEnabled = this.purlSettings.mfaSMSEnabled;

                      const verificationQuestionArray = new UntypedFormArray([]);
                      for (const question of this.purlSettings.verificationQuestions) {
                          verificationQuestionArray.push(this.createQuestion(question));
                      }

                      this.purlSettingsForm.setControl('verificationQuestions', verificationQuestionArray);
                  } else {
                      this.purlSettings = {
                          isPurlEnabled: false,
                          requireMFA: false,
                          verificationText: 'Please try again later',
                          expirationMessageText: 'If you think you’ve gotten this message in error, please reach out to your agent for help.',
                          expirationDays: 30,
                          numberOfRetries: 3,
                          welcomeBodyText: 'To protect your information, we need to ask you a few questions before you can access your application.',
                          welcomeHeaderText: 'Please verify your identity',
                          verificationQuestions: [],
                          mfaQuestionText: 'How do you want to receive your security code?',
                          mfaExpirationTime: 10,                          
                          mfaEmailEnabled: false,
                          mfaEmailQuestionLabel: 'Email me',
                          mfaSMSEnabled: false,
                          mfaSMSQuestionLabel: 'Send me a text message',
                          mfaEmailSubject: 'MFA Token',
                          mfaEmailContent: 'Here is your verification code:  [Verification Code Mapkey]',
                          mfaSMSContent: 'Your verification code is:  [Verification Code Mapkey]',
                          mfaSecurityCodeTitle: 'Enter Your Security Code',
                          mfaSecurityCodeEmailBody: 'Enter the 6 digit code we emailed to your [Email Mapkey].',
                          mfaSecurityCodeSMSBody: 'Enter the 6 digit code we texted to [Phone Number Mapkey].',
                          mfaErrorMessageTitle: 'Information does not match',
                          mfaErrorMessageBody: 'The 6-digit verification code you entered is incorrect. Please visit [URL Mapkey or "signing portal"] to try again.'
                      };
                  }

                  if (!this.purlSettings.requireMFA) {
                      this.purlSettings.mfaQuestionText ='How do you want to receive your security code?';
                      this.purlSettings.mfaExpirationTime= 10;
                      this.purlSettings.mfaEmailEnabled = false;
                      this.purlSettings.mfaEmailQuestionLabel = 'Email me';
                      this.purlSettings.mfaSMSEnabled = false;
                      this.purlSettings.mfaSMSQuestionLabel = 'Send me a text message';
                      this.purlSettings.mfaEmailSubject  = 'MFA Token';
                      this.purlSettings.mfaEmailContent = 'Here is your verification code:  [Verification Code Mapkey]';
                      this.purlSettings.mfaSMSContent = 'Your verification code is:  [Verification Code Mapkey]';
                      this.purlSettings.mfaSecurityCodeTitle = 'Enter Your Security Code';
                      this.purlSettings.mfaSecurityCodeEmailBody = 'Enter the 6 digit code we emailed to your [Email Mapkey].';
                      this.purlSettings.mfaSecurityCodeSMSBody = 'Enter the 6 digit code we texted to [Phone Number Mapkey].';
                      this.purlSettings.mfaErrorMessageTitle = 'Information does not match';
                      this.purlSettings.mfaErrorMessageBody = 'The 6-digit verification code you entered is incorrect. Please visit [URL Mapkey or "signing portal"] to try again.';                      
                  }

                  this.purlSettingsForm.patchValue({
                      isPurlEnabled: this.purlSettings.isPurlEnabled,
                      requireMFA: this.purlSettings.requireMFA,
                      numberOfRetries: this.purlSettings.numberOfRetries,
                      expirationDays: this.purlSettings.expirationDays,
                      welcomeHeaderText: this.purlSettings.welcomeHeaderText,
                      welcomeBodyText: this.purlSettings.welcomeBodyText,
                      mfaQuestionText: this.purlSettings.mfaQuestionText,
                      mfaExpirationTime: this.purlSettings.mfaExpirationTime,
                      mfaTokenMapkey: this.purlSettings.mfaTokenMapkey,
                      mfaEmailSubject: this.purlSettings.mfaEmailSubject,
                      mfaEmailEnabled: this.purlSettings.mfaEmailEnabled,
                      mfaEmailQuestionLabel: this.purlSettings.mfaEmailQuestionLabel,
                      mfaEmailMapkey: this.purlSettings.mfaEmailMapkey,
                      mfaSMSEnabled: this.purlSettings.mfaSMSEnabled,
                      mfaSMSQuestionLabel: this.purlSettings.mfaSMSQuestionLabel,
                      mfaSMSMapkey: this.purlSettings.mfaSMSMapkey,
                      mfaSMSContent: this.purlSettings.mfaSMSContent,
                      mfaSecurityCodeTitle: this.purlSettings.mfaSecurityCodeTitle,
                      mfaErrorMessageTitle: this.purlSettings.mfaErrorMessageTitle
                  });

                  if (!this.purlSettings.expirationMessageText) {
                      this.purlSettings.expirationMessageText = 'If you think you’ve gotten this message in error, please reach out to your agent for help.';
                  }

                  if (!this.purlSettings.verificationText) {
                      this.purlSettings.verificationText = 'Please try again later';
                  }
              }

              this.showForm = true;
          });
  }

  createQuestion(question: PurlQuestion) {
      return this._fb.group({
          id: [question?.id, [Validators.required,RxwebValidators.unique()]],
          verificationText: [question?.verificationText, Validators.required]
      });
  }

  addQuestion() {
      const control = <UntypedFormArray>this.purlSettingsForm.get('verificationQuestions');
      control.push(this.createQuestion(null));
  }

  deleteQuestion(index) {
      const controls = <UntypedFormArray>this.purlSettingsForm.get('verificationQuestions');
      controls.removeAt(index);
  }

  selectQuestion(index) {
      const controls = <UntypedFormArray>this.purlSettingsForm.get('verificationQuestions');
      const control = controls.value[index];
      const question = this.questions.find(x => x.id === control.id);

      (this.verificationQuestions.at(index) as UntypedFormGroup).get('verificationText').patchValue(question.questionText);
  }

  saveSettings() {
      if (this.purlSettingsForm.valid) {
          const purlSettings = this.purlSettingsForm.value as AccountPurlSettings;

          if (purlSettings.isPurlEnabled) {
              const expirationMessageText = this.expirationMessageEditor.textEditorFormGroup.get('htmlEditorControl').value;
              const verificationMessageText = this.verificationAttemptsMessageEditor.textEditorFormGroup.get('htmlEditorControl').value;
              purlSettings.verificationText = verificationMessageText;
              purlSettings.expirationMessageText = expirationMessageText;
          }

          if (purlSettings.requireMFA) {
              purlSettings.mfaErrorMessageBody = this.mfaErrorMessageBodyEditor.textEditorFormGroup.get('htmlEditorControl').value;

              if (purlSettings.mfaEmailEnabled) {
                  purlSettings.mfaSecurityCodeEmailBody = this.mfaSecurityCodeEmailBodyEditor.textEditorFormGroup.get('htmlEditorControl').value;
                  purlSettings.mfaEmailContent = this.mfaEmailContentEditor.textEditorFormGroup.get('htmlEditorControl').value;
              }

              if (purlSettings.mfaSMSEnabled) {
                  purlSettings.mfaSecurityCodeSMSBody = this.mfaSecurityCodeSMSBodyEditor.textEditorFormGroup.get('htmlEditorControl').value;
              }

              if (!purlSettings.mfaSMSEnabled && !purlSettings.mfaEmailEnabled) {
                  this.appService.showMsg('error', 'Plese select at least one MFA verification type.');
                  return;
              }

          }

          if (purlSettings.isPurlEnabled && purlSettings.verificationQuestions.length <= 0) {
              this.appService.showMsg('error', 'Plese select at least one verification question.');
              return;
          }

          if (!purlSettings.isPurlEnabled) {
              purlSettings.verificationQuestions = [];
          }

          this.accountSetting.name = 'purlSettings';
          this.accountSetting.value = JSON.stringify(purlSettings);
          this.accountSettingService.saveAccountSetting(this.accountInfo, this.accountSetting).subscribe(result => {
              this.loadAccountSetting();
              this.appService.showMsg('success', 'Saved successfully ...');
          });
      } else {
          this.appService.showMsg('error', 'Plese fill in all the required fields...');
      }
  } 

  onEnabledToggle(event) {
      this.purlEnabled = event.checked;
  }

  onMFAEmailEnabled(event) {
      if (event.checked) {
          this.mfaEmailEnabled = true;
          this.purlSettingsForm.get('mfaEmailQuestionLabel').addValidators(Validators.required);
          this.purlSettingsForm.get('mfaEmailMapkey').addValidators(Validators.required);
          this.purlSettingsForm.get('mfaEmailSubject').addValidators(Validators.required);
          this.purlSettingsForm.get('mfaEmailContent').addValidators(Validators.required);
          this.purlSettings.mfaEmailContent = 'Here is your verification code:  [Verification Code Mapkey]';
      }else{      
          this.mfaEmailEnabled = false;  
          this.purlSettingsForm.get('mfaEmailQuestionLabel').clearValidators();
          this.purlSettingsForm.get('mfaEmailMapkey').clearValidators();
          this.purlSettingsForm.get('mfaEmailSubject').clearValidators();
          this.purlSettingsForm.get('mfaEmailContent').clearValidators();
      }
  }

  onSMSEmailEnabled(event) {
      if (event.checked) {
          this.mfaSMSEnabled = true;
          this.purlSettingsForm.get('mfaSMSQuestionLabel').addValidators(Validators.required);
          this.purlSettingsForm.get('mfaSMSMapkey').addValidators(Validators.required);
          this.purlSettingsForm.get('mfaSMSContent').addValidators(Validators.required); 
          this.purlSettings.mfaSMSContent = 'Your verification code is:  [Verification Code Mapkey]';          
      }else{        
          this.mfaSMSEnabled = false;
          this.purlSettingsForm.get('mfaSMSQuestionLabel').clearValidators();
          this.purlSettingsForm.get('mfaSMSMapkey').clearValidators();
          this.purlSettingsForm.get('mfaSMSContent').clearValidators();
      }
  }

  onMFAEnabledToggle(event) {
      this.requireMFA = event.checked;

      if (this.requireMFA) {
          this.purlSettingsForm.get('mfaQuestionText').addValidators(Validators.required);
          this.purlSettingsForm.get('mfaExpirationTime').addValidators(Validators.required);
          this.purlSettingsForm.get('mfaTokenMapkey').addValidators(Validators.required);
          this.purlSettingsForm.get('mfaSecurityCodeTitle').addValidators(Validators.required);
          this.purlSettingsForm.get('mfaErrorMessageTitle').addValidators(Validators.required);
      }else{
          this.purlSettingsForm.get('mfaQuestionText').clearValidators();
          this.purlSettingsForm.get('mfaExpirationTime').clearValidators();
          this.purlSettingsForm.get('mfaTokenMapkey').clearValidators();
          this.purlSettingsForm.get('mfaSecurityCodeTitle').clearValidators();
          this.purlSettingsForm.get('mfaErrorMessageTitle').clearValidators();
      }
  }
}
